import { useState, useEffect, useCallback, useMemo } from "react";
import { Input, Switch, Tooltip, Button, Empty, Modal } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  AppstoreOutlined, UnorderedListOutlined, CheckOutlined, CloseOutlined,
  ReloadOutlined, ExclamationCircleFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  fetchSmartSearchPlans, toggleSmartSearchPlan, deleteSmartSearchPlan,
} from "../services/smartSearchService";
import PlanFormDrawer from "./PlanFormDrawer";
import {
  BRAND, limitText, rupees, isActive, tierTone, featureLines,
  isHighlighted, priceDisplay,
} from "./planShared";

/**
 * The Smart Search plan catalogue.
 *
 * Shown as pricing cards by default rather than a table, because these rows are
 * pricing-page copy: a card is the shape the customer will see it in, so the
 * person editing it is looking at the thing they are changing. The dense table
 * is there for the other job — comparing eleven plans' quotas at a glance.
 */

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
];

const chip = (text, tone, extra = {}) => (
  <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 9px", borderRadius: 20,
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.3px", textTransform: "uppercase",
    color: tone.color, background: tone.bg, border: `1px solid ${tone.border}`,
    whiteSpace: "nowrap", ...extra,
  }}>
    {text}
  </span>
);

const CARD = {
  background: "#fff", borderRadius: 18, border: "1px solid #eceef5",
  boxShadow: "0 1px 3px rgba(15,23,42,0.04), 0 12px 30px rgba(15,23,42,0.05)",
  padding: "26px 22px 20px", display: "flex", flexDirection: "column",
  position: "relative", height: "100%", overflow: "hidden",
  transition: "box-shadow .2s ease, transform .2s ease, border-color .2s ease",
  // A long unbroken feature name must never widen the grid column.
  minWidth: 0, overflowWrap: "anywhere",
};

/* A four-pixel strip in the tier's colour along the top edge — the same device
   the subscription plan cards use, because the two catalogues are now tabs of
   one screen and should not look like two products. */
const RAIL = { position: "absolute", top: 0, left: 0, right: 0, height: 4 };

const SECTION_LABEL = {
  fontSize: 10, color: "#94a3b8", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 9,
};

const CardSkeleton = () => (
  <div style={{ ...CARD, minHeight: 360 }}>
    {[["45%", 16], ["68%", 30], ["38%", 12]].map(([w, h], i) => (
      <div key={i} style={{
        width: w, height: h, borderRadius: 7, background: "#f1f4f9",
        marginBottom: 12, animation: "ssPulse 1.3s ease-in-out infinite",
      }} />
    ))}
    <div style={{ height: 1, background: "#f1f4f9", margin: "10px 0 16px" }} />
    {[1, 2, 3, 4].map((i) => (
      <div key={i} style={{
        width: `${88 - i * 7}%`, height: 11, borderRadius: 6, background: "#f4f6fa",
        marginBottom: 10, animation: "ssPulse 1.3s ease-in-out infinite",
      }} />
    ))}
  </div>
);

/** One plan, as the customer would see it. */
const PlanCard = ({ plan, busy, onToggle, onEdit, onDelete }) => {
  const live = isActive(plan);
  const tone = tierTone(plan.tier);
  const f = plan.features || {};
  const lines = featureLines(f);
  const yearly = rupees(plan.yearly_price_inr);
  const subs = Number(plan.active_subscriptions || 0);
  const price = priceDisplay(plan);
  /* A highlighted plan is the one the pricing page pushes, so the card is
     lifted out of the row the same way — an admin comparing these should see
     what the customer will. Only while it is active; a lifted card that
     nobody can buy would be the loudest thing on a screen for no reason. */
  const featured = isHighlighted(f) && live;

  return (
    <div
      style={{
        ...CARD,
        /* Dimmed, not hidden: an inactive plan is still a thing being managed,
           and it has to stay readable to be turned back on. */
        opacity: live ? 1 : 0.62,
        ...(featured ? {
          border: `1.5px solid ${BRAND}`,
          boxShadow: "0 2px 6px rgba(91,72,232,0.12), 0 18px 44px rgba(91,72,232,0.18)",
          transform: "translateY(-4px)",
        } : {
          borderColor: f.badge && live ? "#ddd6fe" : "#eceef5",
        }),
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(15,23,42,0.05), 0 20px 46px rgba(91,72,232,0.16)";
        e.currentTarget.style.transform = featured ? "translateY(-6px)" : "translateY(-3px)";
        if (!featured) e.currentTarget.style.borderColor = tone.border;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = featured
          ? "0 2px 6px rgba(91,72,232,0.12), 0 18px 44px rgba(91,72,232,0.18)"
          : CARD.boxShadow;
        e.currentTarget.style.transform = featured ? "translateY(-4px)" : "";
        if (!featured) e.currentTarget.style.borderColor = f.badge && live ? "#ddd6fe" : "#eceef5";
      }}
    >
      <div style={{ ...RAIL, background: "linear-gradient(90deg," + tone.color + "," + BRAND + ")" }} />
      {f.badge && (
        <div style={{
          position: "absolute", top: 16, right: -6,
          background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`, color: "#fff",
          fontSize: 10, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase",
          padding: "4px 12px", borderRadius: "6px 0 0 6px",
          boxShadow: "0 4px 12px rgba(91,72,232,0.3)",
        }}>
          {f.badge}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap", paddingRight: f.badge ? 86 : 0 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{plan.display_name}</span>
        {chip(plan.tier || "—", tone)}
        {!live && chip("Inactive", { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" })}
      </div>

      {/* What goes here is the price mode's call. A plan sold on enquiry has no
          number, and printing ₹0 against it is not a blank — it is a wrong
          price. */}
      {/* The price sits on its own surface so it reads as the headline rather
          than as the first line of a list. */}
      <div style={{
        background: "linear-gradient(135deg,#fafbff,#f5f6fd)",
        border: "1px solid #eef0f7", borderRadius: 14, padding: "14px 16px",
        marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexWrap: "wrap" }}>
          <span style={{
            fontSize: price.suffix ? 30 : 24, fontWeight: 800, color: "#0f172a",
            letterSpacing: -0.8, fontVariantNumeric: "tabular-nums",
          }}>
            {price.text}
          </span>
          {price.suffix && (
            <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 500 }}>{price.suffix}</span>
          )}
        </div>
        {/* Only stated when there is one, and only when a number is the price at
            all — "Yearly ₹0" under "Contact sales" contradicts the line above. */}
        {yearly && price.suffix && (
          <div style={{
            fontSize: 11.5, color: "#64748b", marginTop: 8, paddingTop: 8,
            borderTop: "1px dashed #e6e9f3",
          }}>
            or <b style={{ color: "#475569" }}>{yearly}</b> /yr
          </div>
        )}
      </div>

      {f.cta && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14, fontStyle: "italic" }}>
          {f.cta}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Searches", value: limitText(plan.monthly_quota, "/mo") },
          { label: "Brands watched", value: limitText(plan.watch_brands_limit) },
        ].map((m) => (
          <div key={m.label} style={{
            flex: 1, minWidth: 0, background: "#fafbfd", border: "1px solid #f0f2f8",
            borderRadius: 11, padding: "10px 12px",
          }}>
            <div style={{ ...SECTION_LABEL, marginBottom: 4 }}>
              {m.label}
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", marginTop: 3 }}>
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {lines.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 16 }}>
          <div style={{ ...SECTION_LABEL, marginBottom: 0 }}>Features</div>
          {/* A feature the plan does not carry still earns a line: seeing what
              is missing is half of how a tier is compared. It is marked, not
              removed — and it is greyed rather than struck through, because the
              ✗ already says it and two markers on one line is noise. */}
          {lines.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{
                width: 16, height: 16, borderRadius: 99, flexShrink: 0, marginTop: 1,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: b.included ? "#eef2ff" : "#f1f5f9",
                color: b.included ? BRAND : "#a0aab8",
              }}>
                {b.included
                  ? <CheckOutlined style={{ fontSize: 9 }} />
                  : <CloseOutlined style={{ fontSize: 9 }} />}
              </span>
              <span style={{
                fontSize: 12.5, lineHeight: 1.45, minWidth: 0, overflowWrap: "anywhere",
                color: b.included ? "#475569" : "#a0aab8",
              }}>
                {b.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pushed to the bottom so every card's actions sit on one line, however
          many bullets it carries. */}
      <div style={{
        marginTop: "auto", paddingTop: 14, borderTop: "1px solid #f1f4f9",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <Tooltip title={live ? "Deactivate this plan" : "Activate this plan"}>
          <Switch
            size="small"
            checked={live}
            loading={busy}
            onChange={onToggle}
            style={live ? { background: BRAND } : undefined}
          />
        </Tooltip>
        {subs > 0 && (
          <Tooltip title="Customers currently on this plan">
            {chip(`${subs.toLocaleString("en-IN")} active`, { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" })}
          </Tooltip>
        )}
        <div style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

/** The same plans, dense — for comparing rather than reading. */
const PlanTable = ({ plans, busyId, onToggle, onEdit, onDelete }) => {
  const th = {
    textAlign: "left", padding: "11px 14px", fontSize: 10.5, fontWeight: 700,
    color: "#8b95a8", textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#fafbff", borderBottom: "1px solid #eef0f6", whiteSpace: "nowrap",
  };
  const td = { padding: "12px 14px", borderBottom: "1px solid #f6f8fc", fontSize: 12.5 };

  return (
    <div style={{
      background: "#fff", borderRadius: 16, border: "1px solid #eceef5",
      overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              {["Plan", "Code", "Tier", "Monthly", "Yearly", "Searches", "Brands", "Subscribers", "Status", ""]
                .map((h, i) => (
                  <th key={i} style={{ ...th, textAlign: i >= 3 && i <= 7 ? "right" : "left" }}>{h}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const live = isActive(p);
              const subs = Number(p.active_subscriptions || 0);
              // Same rule as the card, or the two views state different prices.
              const price = priceDisplay(p);
              const yearly = price.suffix ? rupees(p.yearly_price_inr) : null;
              return (
                <tr key={p.id} style={{ opacity: live ? 1 : 0.6 }}>
                  <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>
                    {p.display_name}
                    {p.features?.badge && (
                      <span style={{ marginLeft: 7 }}>
                        {chip(p.features.badge, { color: BRAND, bg: "#eef2ff", border: "#c7d2fe" })}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: "#64748b", fontFamily: "monospace", fontSize: 11.5 }}>{p.plan_code}</td>
                  <td style={td}>{chip(p.tier || "—", tierTone(p.tier))}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                    {price.text}{price.suffix && <span style={{ color: "#94a3b8", fontWeight: 500 }}>{price.suffix}</span>}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: yearly ? "#475569" : "#cbd5e1", fontVariantNumeric: "tabular-nums" }}>
                    {yearly ?? (price.suffix ? "Not sold" : "—")}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: "#475569" }}>{limitText(p.monthly_quota)}</td>
                  <td style={{ ...td, textAlign: "right", color: "#475569" }}>{limitText(p.watch_brands_limit)}</td>
                  <td style={{ ...td, textAlign: "right", color: subs ? "#059669" : "#cbd5e1", fontWeight: 600 }}>
                    {subs ? subs.toLocaleString("en-IN") : "—"}
                  </td>
                  <td style={td}>
                    <Switch
                      size="small" checked={live} loading={busyId === p.id}
                      onChange={() => onToggle(p)}
                      style={live ? { background: BRAND } : undefined}
                    />
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(p)} style={{ marginRight: 6 }} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(p)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VIEW_KEY = "cms_ss_plans_view";
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "grid"; } catch { return "grid"; }
};

/**
 * @param {boolean} embedded  true when this sits inside another page's tab, in
 *   which case it drops its own page chrome and title — the host already has
 *   both, and a second header inside a tab reads as a page within a page.
 */
export default function ManageSmartSearchPlans({ embedded = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(readView);
  const [drawer, setDrawer] = useState({ open: false, plan: null });
  const [busyId, setBusyId] = useState(null);

  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* not worth failing over */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPlans(await fetchSmartSearchPlans());
    } catch (err) {
      setError(err.message);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Filtered in the browser: this is a catalogue of a handful of plans, not a
     paged list, so a round trip per keystroke would be slower and no more
     correct. */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter((p) => {
      if (filter === "active" && !isActive(p)) return false;
      if (filter === "inactive" && isActive(p)) return false;
      if (!q) return true;
      return [p.plan_code, p.display_name, p.tier]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [plans, search, filter]);

  const counts = useMemo(() => ({
    all: plans.length,
    active: plans.filter(isActive).length,
    inactive: plans.filter((p) => !isActive(p)).length,
  }), [plans]);

  /** Send it, and take the server's word for the result. */
  const runToggle = async (plan, next) => {
    setBusyId(plan.id);
    try {
      const res = await toggleSmartSearchPlan(plan.id, next);
      /* The server owns this value. It is read back rather than assumed,
         because a toggle endpoint that refused, or that had already been
         flipped elsewhere, would otherwise leave the switch showing something
         that is not true. */
      const returned = res?.data?.is_active ?? res?.is_active;
      const settled = returned === undefined ? next : Number(returned);
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: settled } : p)));
      toast.success(res?.msg || res?.message || (settled ? "Plan activated." : "Plan deactivated."));
    } catch (err) {
      // Nothing was moved on screen, so there is nothing to put back.
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Ask before flipping a plan.
   *
   * Deactivating takes a plan off the pricing page, which is a customer-facing
   * change one stray click away — so the dialog states what happens to the
   * people already on it rather than just asking "are you sure".
   */
  const onToggle = (plan) => {
    const live = isActive(plan);
    const next = live ? 0 : 1;
    const subs = Number(plan.active_subscriptions || 0);

    Modal.confirm({
      title: live
        ? `Deactivate "${plan.display_name}"?`
        : `Activate "${plan.display_name}"?`,
      icon: <ExclamationCircleFilled style={{ color: live ? "#d97706" : BRAND }} />,
      content: live
        ? (
          <span>
            This hides the plan from the pricing page, so no new customer can
            subscribe to it.
            {subs > 0 && (
              <>
                {" "}
                <b>
                  {subs.toLocaleString("en-IN")} existing subscription
                  {subs === 1 ? "" : "s"}
                </b>
                {" "}
                will keep running.
              </>
            )}
          </span>
        )
        : "This puts the plan back on the pricing page, where customers can subscribe to it.",
      okText: live ? "Deactivate" : "Activate",
      okButtonProps: live
        ? { danger: true }
        : { style: { background: BRAND, borderColor: BRAND } },
      cancelText: "Cancel",
      onOk: () => runToggle(plan, next),
    });
  };

  const onDelete = (plan) => {
    const subs = Number(plan.active_subscriptions || 0);
    Modal.confirm({
      title: `Delete "${plan.display_name}"?`,
      icon: <ExclamationCircleFilled style={{ color: "#dc2626" }} />,
      content: subs > 0
        ? `${subs} customer${subs === 1 ? " is" : "s are"} subscribed to this plan. Deactivating it hides it from the pricing page without touching them.`
        : "This removes the plan from the catalogue. It cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await deleteSmartSearchPlan(plan.id);
          toast.success(res?.msg || res?.message || "Plan deleted.");
          load();
        } catch (err) {
          toast.error(err.message);

          /* A 409 is the server refusing because people are on this plan.
             Deactivating is what they meant, so it is offered right there —
             and this dialog closes first, or the offer opens behind it. */
          if (err.status === 409) {
            if (isActive(plan)) {
              setTimeout(() => Modal.confirm({
                title: "Deactivate instead?",
                icon: <ExclamationCircleFilled style={{ color: "#d97706" }} />,
                content: "This plan cannot be deleted while customers are subscribed to it. Deactivating hides it from the pricing page and leaves their subscriptions running.",
                okText: "Deactivate",
                cancelText: "Leave it",
                // Straight to the call: this dialog *is* the confirmation.
                onOk: () => runToggle(plan, 0),
              }), 0);
            }
            return;
          }
          // Anything else keeps the dialog open so Delete can be retried.
          throw err;
        }
      },
    });
  };

  const css = `
    @keyframes ssPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
    @keyframes ssFadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    .ss-card { animation: ssFadeUp .3s ease both; }
  `;

  const body = (
    <>
      <style>{css}</style>

      {!embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{
            width: 42, height: 42, borderRadius: 13,
            background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 17, boxShadow: "0 6px 18px rgba(91,72,232,0.3)",
          }}>
            <SearchOutlined />
          </div>
          <div>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#0f172a" }}>Smart Search Plans</div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 2 }}>
              The packages customers buy search quota and brand watches under
            </div>
          </div>
        </div>
      )}

      {/* ── Controls ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap",
              background: "#fff", border: "1px solid #eceef5", borderRadius: 14,
              padding: "12px 14px", boxShadow: "0 1px 3px rgba(15,23,42,0.03)",
            }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: "#c0c6d4" }} />}
                placeholder="Search by name, code or tier"
                style={{ width: 300, borderRadius: 9 }}
              />

              {/* Lives with the filters rather than in the page header, so the
                  action is in the same place whether this is its own page or a
                  tab inside another. */}
              <Button
                type="primary" icon={<PlusOutlined />}
                onClick={() => setDrawer({ open: true, plan: null })}
                style={{
                  background: BRAND, borderColor: BRAND, fontWeight: 600,
                  height: 38, borderRadius: 10,
                  boxShadow: "0 4px 14px rgba(91,72,232,0.28)",
                }}
              >
                New Plan
              </Button>

              <div style={{ display: "inline-flex", gap: 6 }}>
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    style={{
                      padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", whiteSpace: "nowrap",
                      border: `1px solid ${filter === f.key ? "#c7d2fe" : "#e8eaf2"}`,
                      background: filter === f.key ? "#eef2ff" : "#fff",
                      color: filter === f.key ? BRAND : "#64748b",
                    }}
                  >
                    {f.label}
                    <span style={{ marginLeft: 6, opacity: 0.65, fontWeight: 500 }}>{counts[f.key]}</span>
                  </button>
                ))}
              </div>

              <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Tooltip title="Reload">
                  <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }} />
                </Tooltip>
                <div style={{
                  display: "inline-flex", background: "#f4f5fa", borderRadius: 10, padding: 3,
                }}>
                  {[
                    { key: "grid", icon: <AppstoreOutlined />, title: "Pricing cards" },
                    { key: "table", icon: <UnorderedListOutlined />, title: "Dense list" },
                  ].map((v) => (
                    <Tooltip key={v.key} title={v.title}>
                      <button
                        onClick={() => chooseView(v.key)}
                        aria-pressed={view === v.key}
                        style={{
                          border: "none", cursor: "pointer", borderRadius: 8,
                          padding: "6px 13px", fontSize: 13,
                          background: view === v.key ? "#fff" : "transparent",
                          color: view === v.key ? BRAND : "#94a3b8",
                          boxShadow: view === v.key ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                        }}
                      >
                        {v.icon}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Body ── */}
            {loading ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 18 }}>
                {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
            ) : error ? (
              <div style={{
                background: "#fff8f6", border: "1px dashed #f3c6b5", borderRadius: 14,
                padding: "40px 20px", textAlign: "center", color: "#9a3412", fontSize: 13.5,
              }}>
                {error}
                <div style={{ marginTop: 12 }}>
                  <Button onClick={load} icon={<ReloadOutlined />}>Try again</Button>
                </div>
              </div>
            ) : visible.length === 0 ? (
              <div style={{
                background: "#fff", border: "1px solid #eceef5", borderRadius: 16,
                padding: "52px 20px", textAlign: "center",
              }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={(
                    <span style={{ color: "#94a3b8", fontSize: 13 }}>
                      {plans.length === 0
                        ? "No Smart Search plans yet."
                        : "No plan matches this search."}
                    </span>
                  )}
                >
                  {plans.length === 0 && (
                    <Button
                      type="primary" icon={<PlusOutlined />}
                      onClick={() => setDrawer({ open: true, plan: null })}
                      style={{ background: BRAND, borderColor: BRAND, fontWeight: 600 }}
                    >
                      Create the first plan
                    </Button>
                  )}
                </Empty>
              </div>
            ) : view === "grid" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 18 }}>
                {visible.map((p, i) => (
                  <div key={p.id} className="ss-card" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }}>
                    <PlanCard
                      plan={p}
                      busy={busyId === p.id}
                      onToggle={() => onToggle(p)}
                      onEdit={() => setDrawer({ open: true, plan: p })}
                      onDelete={() => onDelete(p)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <PlanTable
                plans={visible}
                busyId={busyId}
                onToggle={onToggle}
                onEdit={(p) => setDrawer({ open: true, plan: p })}
                onDelete={onDelete}
              />
            )}

      <PlanFormDrawer
        open={drawer.open}
        plan={drawer.plan}
        onClose={() => setDrawer({ open: false, plan: null })}
        onSaved={load}
      />
    </>
  );

  /* Embedded, the host page owns the background, padding and title; this just
     hands back its own content. */
  if (embedded) return body;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div style={{
            background: "#f6f7fc", margin: "-24px -24px 0", padding: "26px 28px",
            minHeight: "100vh", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
          }}>
            {body}
          </div>
        </div>
      </div>
    </div>
  );
}
